/* eslint-disable no-unused-vars */
import React, { useState, useMemo, useEffect } from "react";
import PropTypes from "prop-types";
import { Drawer, Input } from "antd";
import { useDispatch, useSelector } from "react-redux";
import useCustomTranslation from "../../hooks/useCustomTranslation";
import Loader from "../../components/Common/Loader";
import { nutrientUnits } from "../../utils/Constant/Constant";
import { Button } from "react-bootstrap";
import ToastNotification from "../../components/Common/ToastNotification";
import {
  resetRecipeState,
  updateRecipeThunk,
} from "../../redux/Slices/updateStandardRecipeSlice";
import { regenerateRecipeImages } from "../../redux/Slices/recipeImageSlice";
import getFileAsBase64 from "../../utils/ImageUtils";
import appInsights from "../../AppInsights";

const RecipeDrawer = ({
  recipeDetails,
  visible,
  onClose,
  onRefresh,
  isToolbox,
  isAdmin,
  fetchData,
}) => {
  const dispatch = useDispatch();
  const { trans } = useCustomTranslation();
  const [loadingImage, setLoadingImage] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [editableRecipeData, seteditableRecipeData] = useState({});
  const [toastConfig, setToastConfig] = useState(null);
  const [selectedImage, setSelectedImage] = useState(null);
  const { loading, success, error } = useSelector(
    (state) => state.updateStandardRecipe
  );
  const { pageNumber, pageSize } = useSelector((state) => state.getRecipes);
  const hasRecipe = !!recipeDetails;
  const recipeData = useMemo(
    () => editableRecipeData || {},
    [editableRecipeData]
  );

  const MISSING_NUTRIENT_FALLBACK = "0";

  // Track recipe drawer open/close
  useEffect(() => {
    if (visible && recipeDetails) {
      appInsights.trackEvent({
        name: "RecipeDrawer_Opened",
        properties: {
          recipeId: recipeDetails.id,
          recipeTitle: recipeDetails.title,
          isToolbox: isToolbox,
          isAdmin: isAdmin,
          isEditMode: isEditMode,
        },
      });
    }

    if (!visible && recipeDetails) {
      appInsights.trackEvent({
        name: "RecipeDrawer_Closed",
        properties: {
          recipeId: recipeDetails.id,
          recipeTitle: recipeDetails.title,
          isEditMode: isEditMode,
          timeSpentInEditMode: isEditMode ? "Yes" : "No",
        },
      });
    }
  }, [visible, recipeDetails, isToolbox, isAdmin, isEditMode]);

  useEffect(() => {
    if (recipeDetails) {
      seteditableRecipeData(JSON.parse(JSON.stringify(recipeDetails)));
    }
  }, [recipeDetails]);

  useEffect(() => {
    const updateBase64ForImages = async () => {
      if (recipeData.mainImage) {
        try {
          const base64Image = await getFileAsBase64(recipeData.mainImage);
          if (base64Image) {
            seteditableRecipeData((prevData) => ({
              ...prevData,
              imageBase64: base64Image,
            }));
          }
        } catch (error) {
          console.error("Error converting profile picture to base64:", error);
        }
      }
    };

    updateBase64ForImages();
  }, [recipeData.mainImage]);

  useEffect(() => {
    const updateBase64ForImages = async () => {
      if (recipeData.mainImage) {
        try {
          const base64Image = await getFileAsBase64(recipeData.mainImage);
          if (base64Image) {
            seteditableRecipeData((prevData) => ({
              ...prevData,
              imageBase64: base64Image,
            }));
          }
        } catch (error) {
          console.error("Error converting profile picture to base64:", error);
        }
      }
    };

    updateBase64ForImages();
  }, [recipeData.mainImage]);

  useEffect(() => {
    if (success) {
      // Track successful recipe update
      appInsights.trackEvent({
        name: "Recipe_Updated_Success",
        properties: {
          recipeId: recipeDetails?.id,
          recipeTitle: recipeDetails?.title,
          isToolbox: isToolbox,
          fieldsUpdated: Object.keys(editableRecipeData || {}).length,
        },
      });

      setToastConfig({
        type: "success",
        message: trans("Common.recipeUpdatedMsg"),
        heading: "Success",
      });
      setIsEditMode(false);
      setTimeout(() => setToastConfig(null), 3000);
    }
    if (error) {
      // Track recipe update failure
      appInsights.trackException({
        exception: new Error(error || "Unknown error"),
        severityLevel: 2,
        properties: {
          name: "Recipe_Update_Failed",
          recipeId: recipeDetails?.id,
          recipeTitle: recipeDetails?.title,
        },
      });

      setToastConfig({
        type: "error",
        message: error || trans("Common.recipeUpdateFailedMsg"),
        heading: "Failed",
      });
      setTimeout(() => setToastConfig(null), 3000);
    }
    dispatch(resetRecipeState());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [success, error, dispatch]);

  const validateInput = (field, value) => {
    if (field.includes("quantity")) {
      if (value === "") return true;
      return /^\d*\.?\d*$/.test(value) && !value.startsWith("-");
    }
    if (field.includes("measure") || field.includes("unit")) {
      return /^[a-zA-Z\s°"'″′]*$/.test(value);
    }
    return true;
  };

  const extractNumericValue = (value) => {
    if (!value) return "";
    const strValue = String(value);

    // If the value is "0" with a unit or just "0", return "0"
    if (strValue === "0" || strValue.startsWith("0 ")) {
      return "0";
    }

    const match = strValue.match(/-?(?:\d+\.?\d*|\.\d+)/);
    return match ? match[0] : "";
  };
  const handleIngredientChange = (index, field, value) => {
    if (!validateInput(field, value)) return;

    const updatedIngredients = [...(editableRecipeData?.ingredientList || [])];
    if (updatedIngredients[index]) {
      updatedIngredients[index] = {
        ...updatedIngredients[index],
        [field]: value,
      };
    }

    seteditableRecipeData((prev) => ({
      ...prev,
      ingredientList: updatedIngredients,
    }));

    // Track ingredient modification
    appInsights.trackEvent({
      name: "Recipe_Ingredient_Modified",
      properties: {
        recipeId: recipeDetails?.id,
        field: field,
        ingredientIndex: index,
        newValue: value,
      },
    });
  };

  const handlePercentageChange = (macro, value) => {
    if (value === "" || /^\d*\.?\d*$/.test(value)) {
      let finalValue = value;

      if (value === "") {
        finalValue = "0";
      } else {
        if (
          finalValue.length > 1 &&
          finalValue.startsWith("0") &&
          !finalValue.startsWith("0.")
        ) {
          finalValue = finalValue.replace(/^0+/, "");
          if (finalValue === "") {
            finalValue = "0";
          }
        }
      }

      if ((value.match(/\./g) || []).length > 1) {
        return;
      }

      seteditableRecipeData((prev) => ({
        ...prev,
        nutrientsPerserving: {
          ...prev.nutrientsPerserving,
          [`${macro}_pct`]: finalValue,
        },
        macroPercentages: {
          ...prev.macroPercentages,
          [macro]: finalValue,
        },
      }));

      // Track macro percentage change
      appInsights.trackEvent({
        name: "Recipe_Macro_Percentage_Changed",
        properties: {
          recipeId: recipeDetails?.id,
          macro: macro,
          newValue: finalValue,
        },
      });
    }
  };

  const handleCancelChanges = () => {
    // Track cancel action
    appInsights.trackEvent({
      name: "Recipe_Edit_Cancelled",
      properties: {
        recipeId: recipeDetails?.id,
        recipeTitle: recipeDetails?.title,
        timeInEditMode: "tracked", // You could add duration tracking here
      },
    });

    const originalData = JSON.parse(JSON.stringify(recipeDetails));

    if (editableRecipeData?.imageBase64) {
      originalData.imageBase64 = editableRecipeData.imageBase64;
    }

    seteditableRecipeData(originalData);
    setIsEditMode(false);
  };

  const handleNutrientChange = (key, value) => {
    seteditableRecipeData((prev) => {
      const input = String(value ?? "").trim();
      const unit = nutrientUnits[key] || "";

      if (input === "" || input === unit || input === `0 ${unit}`.trim()) {
        return {
          ...prev,
          nutrientsPerserving: {
            ...prev.nutrientsPerserving,
            [key]: `0 ${unit}`.trim(),
          },
        };
      }

      const numMatch = input.match(/-?(?:\d+\.?\d*|\.\d+)/);
      let numStr = numMatch ? numMatch[0] : "";

      if (!numMatch && input) {
        if (input === "." || input === "0.") {
          numStr = input;
        } else {
          const parsed = parseFloat(input);
          if (!isNaN(parsed)) {
            numStr = String(parsed);
          } else {
            numStr = "0";
          }
        }
      }

      if (numStr === ".") {
        numStr = "0.";
      } else if (
        numStr.startsWith("0") &&
        !numStr.startsWith("0.") &&
        numStr.length > 1
      ) {
        numStr = numStr.replace(/^0+/, "");
        if (numStr === "" || numStr === ".") numStr = "0";
      }

      if (!numStr || numStr === "-") {
        numStr = "0";
      }

      return {
        ...prev,
        nutrientsPerserving: {
          ...prev.nutrientsPerserving,
          [key]: `${numStr} ${unit}`.trim(),
        },
      };
    });

    // Track nutrient change
    appInsights.trackEvent({
      name: "Recipe_Nutrient_Changed",
      properties: {
        recipeId: recipeDetails?.id,
        nutrient: key,
        newValue: value,
      },
    });
  };
  const handleSaveChanges = () => {
    const { carbs, protein, fat } = recipeData.macroPercentages || {};
    const carbValue = Number(carbs) || 0;
    const proteinValue = Number(protein) || 0;
    const fatValue = Number(fat) || 0;
    const total = carbValue + proteinValue + fatValue;

    if (carbValue <= 0 || proteinValue <= 0 || fatValue <= 0) {
      // Track validation failure
      appInsights.trackException({
        exception: new Error("Recipe_Validation_Failed"),
        severityLevel: 2,
        properties: {
          recipeId: recipeDetails?.id,
          validationType: "macro_values_negative_or_zero",
          carbs: carbValue,
          protein: proteinValue,
          fat: fatValue,
        },
      });

      setToastConfig({
        type: "error",
        message: trans("Common.micronutrientsValidationMsg"),
        heading: "Failed",
      });
      setTimeout(() => setToastConfig(null), 3000);
      return;
    }

    if (total !== 100) {
      // Track validation failure
      appInsights.trackException({
        exception: new Error("Recipe_Validation_Failed"),
        severityLevel: 2,
        properties: {
          recipeId: recipeDetails?.id,
          validationType: "macro_total_not_100",
          total: total,
          carbs: carbValue,
          protein: proteinValue,
          fat: fatValue,
        },
      });

      setToastConfig({
        type: "error",
        message: trans("Common.micronutrientsTotalValidationMsg"),
        heading: "Failed",
      });
      setTimeout(() => setToastConfig(null), 3000);
      return;
    }

    const updatedNutrition = {
      ...editableRecipeData.nutrientsPerserving,
      // percentages (new)
      carbs_pct: String(carbValue),
      protein_pct: String(proteinValue),
      fat_pct: String(fatValue),
    };

    const updatedRecipe = {
      ...editableRecipeData,
      nutrientsPerserving: updatedNutrition,
    };

    seteditableRecipeData(updatedRecipe);
    dispatch(updateRecipeThunk(updatedRecipe))
      .unwrap()
      .then(() => {
        if (typeof onRefresh === "function") {
          setTimeout(() => onRefresh(pageNumber, pageSize), 3000);
        }
      });
  };

  const formatValueWithUnit = (
    val,
    unit,
    fallback = MISSING_NUTRIENT_FALLBACK
  ) => {
    if (val === null || val === undefined) return `${fallback} ${unit}`;
    const strVal = String(val).trim();
    if (strVal === "" || strVal.toLowerCase() === "nan")
      return `${fallback} ${unit}`;
    if (/[a-zA-Z]/.test(strVal)) return strVal;
    return `${strVal} ${unit}`;
  };

  const macroRatios = useMemo(() => {
    const carbsRaw =
      editableRecipeData?.nutrientsPerserving?.carbs_g ||
      editableRecipeData?.nutrientsPerserving?.carbs_pct;
    const proteinRaw =
      editableRecipeData?.nutrientsPerserving?.protein_g ||
      editableRecipeData?.nutrientsPerserving?.protein_pct;
    const fatRaw =
      editableRecipeData?.nutrientsPerserving?.fat_g ||
      editableRecipeData?.nutrientsPerserving?.fat_pct;

    const carbsGrams = parseFloat(carbsRaw) || 0;
    const proteinGrams = parseFloat(proteinRaw) || 0;
    const fatGrams = parseFloat(fatRaw) || 0;

    const totalCalories = proteinGrams * 4 + carbsGrams * 4 + fatGrams * 9;
    if (!totalCalories) return { carbs: 0, protein: 0, fat: 0 };

    return {
      carbs: (carbsGrams * 4) / totalCalories,
      protein: (proteinGrams * 4) / totalCalories,
      fat: (fatGrams * 9) / totalCalories,
    };
  }, [editableRecipeData]);

  const dotColor = {
    carbs: "#89008e",
    protein: "#574dec",
    fat: "#dceb00",
  };

  const formatNutrientKey = (key) =>
    key
      .replace(/([a-z])([A-Z])/g, "$1 $2")
      .replace(/^./, (str) => str.toUpperCase());

  const handleImageUpload = (event) => {
    const file = event.target.files[0];
    if (file) {
      // Track image upload
      appInsights.trackEvent({
        name: "Recipe_Image_Uploaded",
        properties: {
          recipeId: recipeDetails?.id,
          fileName: file.name,
          fileSize: file.size,
          fileType: file.type,
        },
      });

      const reader = new FileReader();
      reader.onloadend = () => {
        const base64String = reader.result;
        const base64Content = base64String.split(",")[1];

        setSelectedImage(base64String);
        seteditableRecipeData((prev) => ({
          ...prev,
          mainImage: base64String,
          recipeImageBase64: base64Content,
          recipeImageFileName: file.name,
          recipeImageMediaType: file.type,
          recipeImageFileType: file.type.split("/")[1],
          recipeImageSourceType: "upload",
        }));
      };
      reader.readAsDataURL(file);
    }
  };
  const handleRegenerateImage = async () => {
    try {
      setLoadingImage(true);

      // Track regenerate image attempt
      appInsights.trackEvent({
        name: "Recipe_Image_Regenerate_Attempted",
        properties: {
          recipeId: recipeDetails?.id,
          recipeTitle: recipeDetails?.title,
        },
      });

      const requestPayload = {
        image: [{ title: editableRecipeData.title }],
      };

      const { payload } = await dispatch(
        regenerateRecipeImages(requestPayload)
      );

      const raw = payload?.items[0];
      let base64String = "";
      if (raw?.imageBase64) {
        base64String = `data:${raw.imageFileType};base64,${raw.imageBase64}`;
      }
      seteditableRecipeData((prev) => ({
        ...prev,
        mainImage: base64String,
        imageBase64: raw.imageBase64 || "",
        recipeImageBase64: raw.imageBase64 || "",
        recipeImageFileName: raw.normalizedTitle,
        recipeImageMediaType: raw.imageFileType,
        recipeImageFileType: raw.imageFileType.split("/")[1],
        recipeImageSourceType: "upload",
      }));

      // Track successful image regeneration
      appInsights.trackEvent({
        name: "Recipe_Image_Regenerated_Success",
        properties: {
          recipeId: recipeDetails?.id,
        },
      });
    } catch (error) {
      console.error("Regenerate image failed:", error);

      // Track image regeneration failure
      appInsights.trackException({
        exception: error,
        severityLevel: 2,
        properties: {
          name: "Recipe_Image_Regenerate_Failed",
          recipeId: recipeDetails?.id,
        },
      });
    } finally {
      setLoadingImage(false);
      setTimeout(() => setToastConfig(null), 3000);
    }
  };

  return (
    <Drawer
      title={
        <div className="d-flex justify-content-between align-items-center mb-2">
          <span>{trans("Common.recipeDetails")}</span>
          {isToolbox &&
            isAdmin &&
            (!isEditMode ? (
              <Button
                type="primary"
                onClick={() => {
                  seteditableRecipeData((prev) => ({
                    ...prev,
                    macroPercentages: {
                      carbs: prev.nutrientsPerserving?.carbs_pct || "0",
                      protein: prev.nutrientsPerserving?.protein_pct || "0",
                      fat: prev.nutrientsPerserving?.fat_pct || "0",
                    },
                  }));
                  setIsEditMode(true);
                }}
                className="saveButton pl-4 pr-4"
              >
                Edit
              </Button>
            ) : (
              <div>
                <Button
                  variant="outline-primary"
                  onClick={handleCancelChanges}
                  className="cancel-button mr-2 pl-1 pr-1"
                >
                  Cancel
                </Button>
                <Button
                  type="primary"
                  onClick={handleSaveChanges}
                  className="saveButton pl-4 pr-4"
                  disabled={loading}
                >
                  {loading ? "Saving..." : "Save"}
                </Button>
              </div>
            ))}
        </div>
      }
      placement="right"
      width="30%"
      onClose={() => {
        setIsEditMode(false);
        // Reset data when closing drawer but preserve imageBase64
        const originalData = JSON.parse(JSON.stringify(recipeDetails));
        if (editableRecipeData?.imageBase64) {
          originalData.imageBase64 = editableRecipeData.imageBase64;
        }
        seteditableRecipeData(originalData);

        // Track drawer close with context
        appInsights.trackEvent({
          name: "RecipeDrawer_Closed_Manual",
          properties: {
            recipeId: recipeDetails?.id,
            wasInEditMode: isEditMode,
          },
        });

        onClose();
      }}
      open={visible}
      className="p-3"
    >
      {!hasRecipe ? (
        <Loader />
      ) : (
        <div>
          {(recipeData?.mainImage || selectedImage) && (
            <div className="position-relative">
              <img
                className="image-card-drawer"
                alt={recipeData.title || "Recipe"}
                src={`data:${
                  editableRecipeData?.imageFileType || "image/png"
                };base64,${editableRecipeData?.imageBase64}`}
                onLoad={() => setLoadingImage(false)}
                onError={() => setLoadingImage(false)}
                style={{ display: loadingImage ? "none" : "block" }}
              />
              {loadingImage && <Loader message="" />}

              {isEditMode && !loadingImage && (
                <>
                  <label htmlFor="image-upload" className="camera-icon-btn">
                    <i className="bi bi-camera"></i>
                  </label>
                  <input
                    id="image-upload"
                    type="file"
                    accept="image/*"
                    onChange={handleImageUpload}
                    style={{ display: "none" }}
                  />
                </>
              )}
            </div>
          )}
          {isEditMode && (
            <div className="mt-2 text-center">
              <Button
                type="primary"
                className="saveButton pl-4 pr-4"
                onClick={handleRegenerateImage}
                disabled={loadingImage}
              >
                {loadingImage ? "Regenerating..." : "Regenerate Image"}
              </Button>
            </div>
          )}
          {/* Title */}
          <h3 className="drawer-recipe-name text-gray-700">
            {recipeData?.title}
          </h3>

          <div className="detail-container-drawer">
            <hr />
            {/* Time / Servings / Ingredients */}
            <div className="d-flex">
              <p className="detail-time-recipe">
                {(recipeData?.prepTime || recipeData?.totalTime) ?? "N/A"}
              </p>
              <p className="detail-time-recipe">
                {`${recipeData?.servings ?? 0}`}
              </p>
              <p className="detail-time-recipe">
                {`${recipeData?.ingredientsCount ?? 0} ${trans(
                  "recipes.ingredients"
                )}`}
              </p>
            </div>

            <hr className="mt-0" />

            {/* Ingredients */}
            <h4>{trans("recipes.Ingredients")}</h4>
            <ul className="blue-dot-list">
              {(editableRecipeData?.ingredientList || []).map((item, idx) =>
                !isEditMode ? (
                  <li key={idx} className="fs-5">
                    {`${item.quantityMetric} ${item.unitMetric} ${
                      item?.ingredient || " "
                    }`}
                  </li>
                ) : (
                  <li key={idx} className="fs-5 d-flex align-items-center">
                    <div className="mb-3">
                      <div className="d-flex align-items-center gap-2 mb-2">
                        <span className="fw-bold">{item?.ingredient}</span>
                      </div>
                      <div className="row g-2">
                        <div className="col-6">
                          <label className="form-label small">Metric</label>
                          <div className="d-flex gap-1">
                            <Input
                              value={item?.quantityMetric || ""}
                              onChange={(e) =>
                                handleIngredientChange(
                                  idx,
                                  "quantityMetric",
                                  e.target.value
                                )
                              }
                              size="small"
                              placeholder="Qty"
                              style={{ width: "60px" }}
                            />
                            <Input
                              value={item?.unitMetric || ""}
                              onChange={(e) =>
                                handleIngredientChange(
                                  idx,
                                  "unitMetric",
                                  e.target.value
                                )
                              }
                              size="small"
                              placeholder="Unit"
                              style={{ width: "60px" }}
                            />
                          </div>
                        </div>
                        <div className="col-6">
                          <label className="form-label small">Imperial</label>
                          <div className="d-flex gap-1">
                            <Input
                              value={item?.quantityImperial || ""}
                              onChange={(e) =>
                                handleIngredientChange(
                                  idx,
                                  "quantityImperial",
                                  e.target.value
                                )
                              }
                              size="small"
                              placeholder="Qty"
                              style={{ width: "60px" }}
                            />
                            <Input
                              value={item?.unitImperial || ""}
                              onChange={(e) =>
                                handleIngredientChange(
                                  idx,
                                  "unitImperial",
                                  e.target.value
                                )
                              }
                              size="small"
                              placeholder="Unit"
                              style={{ width: "60px" }}
                            />
                          </div>
                        </div>
                      </div>
                      <hr />
                    </div>
                  </li>
                )
              )}
            </ul>

            {/* Instructions */}
            <h4>{trans("recipes.instructions")}</h4>
            <ul className="blue-dot-list">
              {(editableRecipeData?.instructions || []).flatMap((instr, idx) =>
                instr
                  .split(",,")
                  .filter((step) => step.trim().length > 0)
                  .map((step, stepIdx) => (
                    <li key={`${idx}-${stepIdx}`} className="fs-5">
                      {formatNutrientKey(step.trim())}
                    </li>
                  ))
              )}
            </ul>

            <hr />
            {/* Nutrition */}
            <h4>{trans("recipes.nutritionPerServing")}</h4>

            {/* Macro progress bar */}
            <div className="mt-2 progress-bar-recipe mb-3">
              <div
                className="bar-carb"
                style={{ width: `${(macroRatios.carbs || 0) * 100}%` }}
              />
              <div
                className="bar-protein"
                style={{ width: `${(macroRatios.protein || 0) * 100}%` }}
              />
              <div
                className="bar-fat"
                style={{ width: `${(macroRatios.fat || 0) * 100}%` }}
              />
            </div>

            {/* Macro indicators */}
            {["carbs", "protein", "fat"].map((macro) => (
              <React.Fragment key={macro}>
                <div className="indicator-nutrition d-flex align-items-center">
                  <div
                    className="rounded-circle ml-2 mr-2"
                    style={{
                      backgroundColor: dotColor[macro],
                      width: 15,
                      height: 15,
                    }}
                  />

                  <span className="fs-5">
                    {trans(`recipes.${macro}`)} (
                    {isEditMode ? (
                      <Input
                        value={
                          editableRecipeData?.nutrientsPerserving?.[
                            `${macro}_pct`
                          ] || ""
                        }
                        onChange={(e) =>
                          handlePercentageChange(macro, e.target.value)
                        }
                        suffix="%"
                        size="small"
                        style={{ width: 80 }}
                      />
                    ) : (
                      `${
                        editableRecipeData?.nutrientsPerserving?.[
                          `${macro}_pct`
                        ] || ((macroRatios?.[macro] || 0) * 100).toFixed(1)
                      }%`
                    )}
                    )
                  </span>

                  <div className="ml-auto mb-0">
                    {isEditMode ? (
                      <Input
                        value={extractNumericValue(
                          editableRecipeData?.nutrientsPerserving?.[
                            `${macro}_g`
                          ] || editableRecipeData?.nutrientsPerserving?.[macro]
                        )}
                        onChange={(e) =>
                          handleNutrientChange(
                            `${macro}_g`,
                            `${e.target.value}g`
                          )
                        }
                        suffix="g"
                        size="small"
                        style={{ width: 80 }}
                      />
                    ) : (
                      <p className="mb-0">
                        {formatValueWithUnit(
                          editableRecipeData?.nutrientsPerserving?.[
                            `${macro}_g`
                          ] || editableRecipeData?.nutrientsPerserving?.[macro],
                          "g"
                        )}
                      </p>
                    )}
                  </div>
                </div>
                <hr />
              </React.Fragment>
            ))}
            <ul>
              {Object.entries(editableRecipeData?.nutrientsPerserving || {})
                .filter(([key]) =>
                  [
                    "calories",
                    "calcium",
                    "iron",
                    "magnesium",
                    "potassium",
                    "sodium",
                    "sugar",
                    "vitaminC",
                    "fiber",
                  ].includes(key)
                )
                .map(([key, value]) => {
                  const formattedKey = formatNutrientKey(key);
                  const unit = nutrientUnits[key] || "";

                  return (
                    <li key={key} className="p-0 m-0">
                      <div className="d-flex justify-content-between align-items-center">
                        <p>{formattedKey}</p>
                        <div>
                          {isEditMode ? (
                            <Input
                              value={extractNumericValue(value)}
                              onChange={(e) =>
                                handleNutrientChange(
                                  key,
                                  `${e.target.value}${unit}`
                                )
                              }
                              suffix={unit}
                              size="small"
                              style={{ width: 80 }}
                            />
                          ) : (
                            <p>{formatValueWithUnit(value, unit)}</p>
                          )}
                        </div>
                      </div>
                    </li>
                  );
                })}
            </ul>
          </div>
        </div>
      )}
      {toastConfig && (
        <ToastNotification
          type={toastConfig.type}
          message={toastConfig.message}
          heading={toastConfig.heading}
        />
      )}
    </Drawer>
  );
};

RecipeDrawer.propTypes = {
  visible: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  recipeDetails: PropTypes.object,
  onRefresh: PropTypes.func,
};

export default RecipeDrawer;
